import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { AvatarUploadField } from '@/components/common/AvatarUploadField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { initialsFromName } from '@/lib/utils/user.utils';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import type { InvestorProfileFormValues } from './schema';
import type { InvestorSectionId } from './investor-sections';
import {
  INVESTOR_CONTACT_PHOTO_CAPTION,
  INVESTOR_CONTACT_PHOTO_LABEL,
} from './investor-form.constants';
import { IdentitySection } from './IdentitySection';
import { LocationSection } from './LocationSection';
import { PreferencesSection } from './PreferencesSection';
import { FounderPreferencesSection } from './FounderPreferencesSection';
import { IndustriesSection } from './IndustriesSection';
import { GeographiesSection } from './GeographiesSection';
import { ColleagueContactsSection } from './ColleagueContactsSection';
import { InvestorColleaguesCard } from './InvestorColleaguesCard';
import {
  FounderPreferencesCard,
  WhatWeInvestInCard,
  type InvestorCardProps,
} from './InvestorPreferenceCards';
import {
  InvestorAboutCard,
  InvestorGeographiesCard,
  InvestorLocationCard,
} from './InvestorContactCards';

const S = UI_STRINGS.INVESTOR_PROFILE;

export interface InvestorSectionEditProps {
  data: InvestorProfileData;
  form: UseFormReturn<InvestorProfileFormValues>;
  trackUpload: (id: string) => (isUploading: boolean) => void;
  /** `iso2` of the person's own country, where the phone picker starts (FR-184). */
  defaultPhoneCountryIso2?: string;
}

/** Each card's read view, laid out as in the client-approved mockup (FR-175). */
export const INVESTOR_SECTION_VIEWS: Record<
  InvestorSectionId,
  (props: InvestorCardProps) => ReactNode
> = {
  about: (p) => <InvestorAboutCard {...p} />,
  preferences: (p) => <WhatWeInvestInCard {...p} />,
  founder: (p) => <FounderPreferencesCard {...p} />,
  location: (p) => <InvestorLocationCard {...p} />,
  geographies: (p) => <InvestorGeographiesCard {...p} />,
  colleagues: (p) => <InvestorColleaguesCard {...p} />,
};

/** The contact headshot upload, moved from the old header card into the About panel. */
function ContactPhotoField({ data, form, trackUpload }: InvestorSectionEditProps) {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel label={INVESTOR_CONTACT_PHOTO_LABEL} variant="caps" />
      <AvatarUploadField
        photoUrl={data.profilePhotoUrl ?? undefined}
        initials={initialsFromName(data.contactName || data.firmName || '')}
        uploadKind="investorProfilePhoto"
        accept="image/png,image/jpeg"
        uploadCaption={INVESTOR_CONTACT_PHOTO_CAPTION}
        tooLargeMessage={S.PHOTO_TOO_LARGE}
        errorMessage={S.PHOTO_UPLOAD_ERROR}
        onUploaded={(result) =>
          form.setValue('profilePhotoFileKey', result.fileKey, { shouldDirty: true })
        }
        onUploadingChange={trackUpload('photo')}
      />
    </div>
  );
}

/** Each side panel's fields: the same section component in `edit` mode, bound to the panel's form. */
export const INVESTOR_SECTION_EDITORS: Record<
  InvestorSectionId,
  (props: InvestorSectionEditProps) => ReactNode
> = {
  about: (props) => (
    <IdentitySection
      photoSlot={<ContactPhotoField {...props} />}
      data={props.data}
      control={props.form.control}
      register={props.form.register}
      errors={props.form.formState.errors}
      setValue={props.form.setValue}
      onLogoUploadingChange={props.trackUpload('logo')}
      defaultPhoneCountryIso2={props.defaultPhoneCountryIso2}
    />
  ),
  location: ({ form }) => (
    <LocationSection
      control={form.control}
      register={form.register}
      errors={form.formState.errors}
    />
  ),
  preferences: ({ form }) => (
    <div className="flex flex-col gap-8">
      <PreferencesSection
        control={form.control}
        register={form.register}
        errors={form.formState.errors}
      />
      <div className="flex flex-col gap-2">
        <FieldLabel label={S.INDUSTRIES} variant="caps" />
        <IndustriesSection control={form.control} />
      </div>
    </div>
  ),
  founder: ({ form }) => (
    <FounderPreferencesSection
      control={form.control}
      register={form.register}
      errors={form.formState.errors}
    />
  ),
  geographies: ({ form }) => (
    <GeographiesSection
      control={form.control}
      register={form.register}
      errors={form.formState.errors}
      setValue={form.setValue}
    />
  ),
  colleagues: ({ data, form }) => (
    <ColleagueContactsSection
      mode="edit"
      data={data}
      control={form.control}
      register={form.register}
      errors={form.formState.errors}
    />
  ),
};
